import useAuthUserStore from '@/modules/auth/stores/useAuthUserStore';
import useTokenStore from '@/modules/auth/stores/useTokenStore';
import type { FileRoutesByTo } from '@/modules/router/constants/routeTree.gen';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { trimEnd, trimStart } from 'lodash-es';
import { useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';

export default function useLogoutUser() {
  const clearTokens = useTokenStore((state) => state.clearTokens);
  const clearUser = useAuthUserStore((state) => state.clearUser);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  return useCallback(
    async ({
      redirect,
      ref,
      action,
      reason,
    }: {
      redirect?: keyof FileRoutesByTo | undefined;
      ref?: string | undefined;
      action?: 'logout' | 'force_logout';
      reason?: 'unauthorized' | 'session_expired' | 'refresh_failed';
    } = {}) => {
      clearTokens();
      clearUser();
      Sentry.setUser(null);
      queryClient
        .getQueryCache()
        .findAll()
        .filter((query) => query.queryKey[0] !== 'adminSetting')
        .forEach((cache) => {
          queryClient.removeQueries({ queryKey: cache.queryKey });
        });

      const withoutEdgeSlashes = trimEnd(
        trimStart(location.pathname, '/'),
        '/'
      );
      return navigate({
        to: redirect ?? '/auth/login',
        search: {
          ref: ref ?? withoutEdgeSlashes.replace(/\//g, '_'),
          action: action ?? 'logout',
          reason,
          redirect,
        },
        replace: true,
      });
    },
    [clearTokens, clearUser, location.pathname, navigate, queryClient]
  );
}
